/**
 * Beta panel (lane beta-panel, 2026-09-12): the header's "Beta" pill and its
 * sheet. Scott's mandate: move the bug icon up by the hamburger and make it
 * something a tester can use, not developer tooling.
 *
 * Phase 2: the data is the shell's `betaDiagnostics` answer (method name copied
 * from ios/swimset/Bridge/BridgeRouter.swift). The pill renders only when the
 * shell lists that method, or when a fixture is named, so a browser or an older
 * shell never shows made-up diagnostics:
 *   - `window.__SWIMSET_BETA_FIXTURE__ = "green" | "red"` (snapshot scripts), or
 *   - `?beta=green|red` in the URL (a desktop browser).
 */
import type { BetaDiagnosticsResponse } from "@contract/types";
import { debugSearch } from "../debugSearch";

export type ClubRole = "leader" | "member" | "none" | "unknown";

/** What the sheet shows and what "Copy diagnostics" copies. No names, no swim content. */
export interface BetaDiagnostics {
  /** date + whatsNew are null for a build with no entry in BUILD_NOTES. */
  build: { number: number; appVersion: string; date: string | null; whatsNew: [string, string] | null };
  iosVersion: string;
  deviceModel: string;
  web: { sha: string; cache: string };
  health: "allowed" | "not allowed" | "not asked";
  /** ISO date of the most recent swim the watch recorded, or null for none. */
  lastSwimDate: string | null;
  lastSwimPoolMeters: number | null;
  icloud: "signed in" | "signed out" | "restricted" | "unknown";
  /**
   * memberCount counts you. rosterSynced / ownSwimSynced are the content check
   * (orchestrator 2026-09-12): the Club row is green only when one is true.
   * ownSwimSynced = at least one of your swims had a CONFIRMED save to the
   * club (build 10, B-015); before that it read a store row that never existed.
   * lastSync null = no successful club fetch recorded on this phone.
   */
  club: {
    role: ClubRole;
    memberCount: number | null;
    rosterSynced: boolean;
    ownSwimSynced: boolean;
    lastSync: string | null;
  };
  locale: string;
}

/** Two lines per build from AppStore/v0.1.0/build-notes/build-N.md, in swimmer words. */
export const BUILD_NOTES: Record<number, { date: string; whatsNew: [string, string] }> = {
  5: { date: "2026-09-12", whatsNew: ["Four tabs: Swim, Sets, Strokes, Club", "Share a swim or a set as a picture"] },
  6: { date: "2026-09-12", whatsNew: ["Beta pill by the menu: send feedback, check your setup", "Red rows get a one-tap fix; diagnostics carry no names or swims"] },  7: { date: "2026-09-12", whatsNew: ["Stroke fixes show on every screen and reach the club", "A stale tab can no longer undo an edit"] },
  8: { date: "2026-09-12", whatsNew: ["Club tab gets the calendar: swimmer counts per day", "Exports carry the watch model and watchOS version"] },
  9: { date: "2026-09-13", whatsNew: ["Club history republishes after a beta-over-dev install", "Health off, no Mail, no club: every path now says what to do"] },
  10: { date: "2026-09-13", whatsNew: ["Clubmates see your name again; change it in Club settings", "Swims go to the club when the app opens and when sharing turns on"] },
  11: { date: "2026-09-13", whatsNew: ["Club calendar shows your club's whole history", "One odd swim no longer hides the rest of the club"] },
  12: { date: "2026-09-13", whatsNew: ["A swim with no lengths says so, with its distance and time", "Club calendar updates while it is open"] },
  13: { date: "2026-09-14", whatsNew: ["Tap a swim in Club: its sets, then everyone who swam that day", "One card, one row of reactions; the separate Sets today block is gone"] },
  14: { date: "2026-09-14", whatsNew: ["Club's top matches Swim: date row first, settings gear by the ring", "Text fades under the tab bar; the date fits on first launch"] },
};

export type BetaFixtureName = "green" | "red";

const GREEN: BetaDiagnostics = {
  build: { number: 5, appVersion: "0.1.0", ...BUILD_NOTES[5] },
  iosVersion: "26.0",
  deviceModel: "iPhone 16",
  web: { sha: "2e7a931", cache: "latest" },
  health: "allowed",
  // The stills harness moves the Swim tab's swim onto today (2026-09-12 for
  // these stills); the sheet has to agree with the scene behind it, or it
  // reads "Last swim Wed, Sep 9" over a swim recorded today (VP round 3).
  lastSwimDate: "2026-09-12",
  lastSwimPoolMeters: 25,
  icloud: "signed in",
  club: { role: "member", memberCount: 3, rosterSynced: true, ownSwimSynced: true, lastSync: "2026-09-12T13:58:00" },
  locale: "en_US",
};

// One red row, and it is the case behind "the Club tab shows nothing" that a
// single iCloud-and-club row could not see (VP rounds 1-2; orchestrator ruling
// A, 2026-09-12): iCloud is fine, nothing from the club has reached this phone.
const RED: BetaDiagnostics = {
  ...GREEN,
  club: { role: "member", memberCount: null, rosterSynced: false, ownSwimSynced: false, lastSync: null },
};

export const BETA_FIXTURES: Record<BetaFixtureName, BetaDiagnostics> = { green: GREEN, red: RED };

export function parseBetaFixture(search: string, injected: unknown): BetaFixtureName | null {
  if (injected === "green" || injected === "red") return injected;
  const raw = new URLSearchParams(search).get("beta");
  return raw === "green" || raw === "red" ? raw : null;
}

/** A named fixture, or null (then the real shell answer is the only source). */
export function betaFixture(): BetaDiagnostics | null {
  if (typeof window === "undefined") return null;
  const injected = (window as unknown as Record<string, unknown>).__SWIMSET_BETA_FIXTURE__;
  const name = parseBetaFixture(debugSearch(), injected);
  return name ? BETA_FIXTURES[name] : null;
}

/**
 * The shell's answer in the sheet's shape. The web build line is the page's own
 * sha plus what the shell knows about it (handshake `webBuildIsLatest`).
 */
export function fromBridge(
  r: BetaDiagnosticsResponse,
  webSha: string,
  webIsLatest: boolean | null,
): BetaDiagnostics {
  const number = Number.parseInt(r.buildNumber, 10) || 0;
  const notes = BUILD_NOTES[number] ?? null;
  return {
    build: { number, appVersion: r.appVersion, date: notes?.date ?? null, whatsNew: notes?.whatsNew ?? null },
    iosVersion: r.iosVersion,
    deviceModel: r.deviceModel,
    web: {
      sha: webSha === "unknown" ? "unknown" : webSha.slice(0, 7),
      cache: webIsLatest === true ? "latest" : webIsLatest === false ? "newer available" : "unknown",
    },
    health: r.health,
    lastSwimDate: r.lastSwimDate ?? null,
    lastSwimPoolMeters: r.lastSwimPoolMeters ?? null,
    icloud: r.icloud,
    club: {
      role: r.club.role,
      memberCount: r.club.memberCount ?? null,
      rosterSynced: r.club.rosterSynced,
      ownSwimSynced: r.club.ownSwimSynced,
      lastSync: r.club.lastSync ?? null,
    },
    locale: r.locale,
  };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "2026-09-09" → "Wed, Sep 9". Read as a local calendar day, never shifted by UTC. */
export function shortDay(isoDate: string): string {
  const [y, m, d] = isoDate.slice(0, 10).split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return `${DAYS[date.getDay()]}, ${MONTHS[m - 1]} ${d}`;
}

/** "2026-09-12T13:58:00" → "1:58 PM". */
export function clockTime(isoLocal: string): string {
  const [hh, mm] = isoLocal.slice(11, 16).split(":").map(Number);
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;
}

/** "today 1:58 PM" on the same calendar day as `now`, else "Fri, Sep 11" (VP round 1: a bare time reads as maybe stale). */
export function syncWhen(isoLocal: string, now: Date): string {
  return sameDay(isoLocal, now) ? `today ${clockTime(isoLocal)}` : shortDay(isoLocal);
}

/** The ISO date/datetime falls on `now`'s local calendar day. */
function sameDay(iso: string, now: Date): boolean {
  return (
    Number(iso.slice(0, 4)) === now.getFullYear() &&
    Number(iso.slice(5, 7)) === now.getMonth() + 1 &&
    Number(iso.slice(8, 10)) === now.getDate()
  );
}

export type SetupFix = "Turn on" | "How to" | "Settings" | "Invite" | "Club settings";

/**
 * What "Turn on" does. iOS shows the Health sheet once; after any answer it shows
 * nothing, so an already-asked phone goes to Settings instead. HealthKit never
 * reports a read denial: "not allowed" is "asked, and no swim is readable".
 */
export function healthFixAction(health: BetaDiagnostics["health"]): "request" | "settings" {
  return health === "not asked" ? "request" : "settings";
}

export interface SetupRow {
  key: "health" | "watch" | "icloud" | "club";
  title: string;
  ok: boolean;
  detail: string;
  /** The one-tap fix, shown only on a red row. null on a red row = fix the row above first. */
  fix: SetupFix | null;
  /** Inline text the "How to" fix expands; null for every other fix. */
  help: string | null;
  /** A red, scoped second line under a neutral detail (the unsynced Club row). */
  alert?: string;
}

/** Segments wrap only between " · "; words inside one are glued with no-break spaces. */
function segments(parts: string[]): string {
  return parts.map((p) => p.replace(/ /g, "\u00A0")).join(" · ");
}

/** What "How to" on the Apple Watch row expands to. No native action exists or is needed. */
export const WATCH_HELP =
  "On your Apple Watch, open Workout and start Pool Swim or Open Water. When you finish, the swim shows up here.";

/**
 * Four rows (orchestrator ruling A, 2026-09-12): iCloud and the club are
 * separate checks, so a signed-in phone whose club never synced is red on the
 * Club row instead of green on a merged one.
 */
export function setupRows(d: BetaDiagnostics, now: Date = new Date()): SetupRow[] {
  const healthOk = d.health === "allowed";
  const watchOk = d.lastSwimDate !== null;
  const icloudOk = d.icloud === "signed in";
  return [
    {
      key: "health",
      title: "Health access",
      ok: healthOk,
      detail: healthOk
        ? "Swim Set Club can read your swims"
        : d.health === "not allowed"
          ? "Turned off in\u00A0Settings"
          : "Swim Set Club can't read your swims",
      fix: healthOk ? null : "Turn on",
      help: null,
    },
    {
      key: "watch",
      title: "Apple Watch swims",
      ok: watchOk,
      detail: watchOk ? `Last swim ${sameDay(d.lastSwimDate!, now) ? "today" : shortDay(d.lastSwimDate!)}` : "No swims found yet",
      fix: watchOk ? null : "How to",
      help: watchOk ? null : WATCH_HELP,
    },
    {
      key: "icloud",
      title: "iCloud account",
      ok: icloudOk,
      detail: icloudOk
        ? "Signed in on this iPhone"
        : d.icloud === "restricted"
          ? "Turned off for Swim Set Club"
          : d.icloud === "unknown"
            ? "Couldn't check just now"
            : "Not signed in, and the club needs it",
      fix: icloudOk || d.icloud === "unknown" ? null : "Settings",
      help: null,
    },
    clubRow(d, icloudOk, now),
  ];
}

function clubRow(d: BetaDiagnostics, icloudOk: boolean, now: Date): SetupRow {
  const base = { key: "club" as const, title: "Club", help: null };
  if (!icloudOk) return { ...base, ok: false, detail: "Needs iCloud first", fix: null };
  const { role, memberCount, rosterSynced, ownSwimSynced, lastSync } = d.club;
  if (role === "none") return { ...base, ok: false, detail: "Not in a club yet", fix: "Invite" };
  if (role === "unknown") return { ...base, ok: false, detail: "Couldn't check your club just now", fix: null };
  // Content check: a role alone is not a working club. Green needs something
  // that actually arrived, the roster or your own swim.
  // There is no bridge to start a sync, so the row says when the phone last
  // reached the club, and its fix opens the native club sheet: the "Share my
  // swims with the club" toggle lives there (ClubSettingsView), and a member who
  // never turned it on never syncs (orchestrator fix round, 2026-09-12).
  if (!rosterSynced && !ownSwimSynced) {
    // Beside the button the detail wraps; glue the tail so no word is left alone
    // on a line ("yet" orphaned at 390pt, 2026-09-12 still; the "PM" rule above).
    // VP copy round 2: keep the membership on the row (neutral) and scope the red
    // line to what is wrong, so it cannot read as "removed from the club" or as
    // the whole checklist failing. The count shows only when above 1: a phone
    // that synced nothing knows only you.
    const parts = [role === "leader" ? "Leader" : "Member"];
    if (memberCount !== null && memberCount > 1) parts.push(`${memberCount} swimmers`);
    if (lastSync !== null) parts.push(`last sync ${syncWhen(lastSync, now)}`);
    return {
      ...base,
      ok: false,
      detail: segments(parts),
      alert: "Your swims haven't reached the\u00A0club",
      fix: "Club settings",
    };
  }
  if (memberCount !== null && memberCount <= 1) return { ...base, ok: false, detail: "Only you so far", fix: "Invite" };
  const parts = [role === "leader" ? "Leader" : "Member"];
  if (memberCount !== null) parts.push(`${memberCount} swimmers`);
  if (lastSync !== null) parts.push(`synced ${syncWhen(lastSync, now)}`);
  // Wrap only between segments: at 390pt the line broke inside the clock and
  // left "PM" alone on its own line. Words inside a segment are glued with
  // no-break spaces so "synced today 1:58 PM" moves down as one piece.
  return { ...base, ok: true, detail: segments(parts), fix: null };
}

/** The block "Copy diagnostics" copies and the feedback email carries. */
export function formatDiagnostics(d: BetaDiagnostics): string {
  const members = d.club.memberCount === null ? "members unknown" : `${d.club.memberCount} members`;
  const yn = (b: boolean) => (b ? "yes" : "no");
  const club =
    d.club.role === "none"
      ? "not in a club"
      : `${d.club.role}, ${members}, roster synced ${yn(d.club.rosterSynced)}, own swims synced ${yn(d.club.ownSwimSynced)}, last sync ${d.club.lastSync ?? "never"}`;
  return [
    `Swim Set Club beta build ${d.build.number} (${d.build.appVersion})`,
    `iOS ${d.iosVersion}, ${d.deviceModel}`,
    `Web build ${d.web.sha}, ${d.web.cache}`,
    `Health access: ${d.health}`,
    `iCloud: ${d.icloud}`,
    `Club: ${club}`,
    `Last swim pool: ${d.lastSwimPoolMeters === null ? "none" : `${d.lastSwimPoolMeters} m`}`,
    `Locale: ${d.locale}`,
  ].join("\n");
}

/** The feedback email body: room to write on top, the diagnostics underneath. */
export function feedbackBody(d: BetaDiagnostics): string {
  return `What happened?\n\n\n\nApp details (no names or swims):\n${formatDiagnostics(d)}\n`;
}
